
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PorterContextType {
  isConnected: boolean;
  workspaceId: string;
  reportId: string | null;
  setConnected: (connected: boolean) => void;
  setWorkspaceId: (id: string) => void;
  setReportId: (id: string | null) => void;
  disconnect: () => void;
}

const PorterContext = createContext<PorterContextType | undefined>(undefined);

const DEFAULT_WORKSPACE_ID = "demo_workspace"; // Replace with your actual workspace ID
const DEFAULT_REPORT_ID = "demo_report"; // Replace with your actual report ID

export const PorterProvider = ({ children }: { children: ReactNode }) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [workspaceId, setWorkspaceId] = useState<string>(DEFAULT_WORKSPACE_ID);
  const [reportId, setReportId] = useState<string | null>(DEFAULT_REPORT_ID);

  // Load connection state from localStorage on initial render
  useEffect(() => {
    const connectionStatus = localStorage.getItem('porter_meta_connected');
    const storedWorkspaceId = localStorage.getItem('porter_workspace_id');
    const storedReportId = localStorage.getItem('porter_report_id');
    
    if (connectionStatus === 'true') {
      setIsConnected(true);
    }
    
    if (storedWorkspaceId) {
      setWorkspaceId(storedWorkspaceId);
    }
    
    if (storedReportId) {
      setReportId(storedReportId);
    }
  }, []);

  // Save connection state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('porter_meta_connected', isConnected ? 'true' : 'false');
  }, [isConnected]);

  // Save workspace ID to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('porter_workspace_id', workspaceId);
  }, [workspaceId]);

  // Save report ID to localStorage whenever it changes
  useEffect(() => {
    if (reportId) {
      localStorage.setItem('porter_report_id', reportId);
    } else {
      localStorage.removeItem('porter_report_id');
    }
  }, [reportId]);

  const setConnected = (connected: boolean) => {
    setIsConnected(connected);
  };

  const disconnect = () => {
    localStorage.removeItem('porter_meta_connected');
    setIsConnected(false);
  };

  return (
    <PorterContext.Provider value={{ 
      isConnected, 
      workspaceId, 
      reportId,
      setConnected, 
      setWorkspaceId,
      setReportId,
      disconnect 
    }}>
      <div className="bg-[#151515] bg-grid-pattern bg-fixed">
        <div className="min-h-screen">
          {children}
        </div>
      </div>
    </PorterContext.Provider>
  );
};

export const usePorter = () => {
  const context = useContext(PorterContext);
  if (context === undefined) {
    throw new Error('usePorter must be used within a PorterProvider');
  }
  return context;
};
